import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../shared/store/auth.store";
import { useProfileStore } from "../../shared/store/profile.store";
import { AuthService } from "../../shared/services/auth.service";
import { getMaxAuthPayload } from "../../shared/services/init-data.service";
import { ButtonLarge } from "../../shared/uikit";
import { Loader } from "../../shared/uikit";
import {
  consumePendingToken,
  hasPendingToken,
} from "../../shared/services/deep-link.service";
import { ConfigRoutes, getPath } from "../../routes/routes.path";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { setTokens, setAuthenticated } = useAuthStore.getState();
  const { setProfile, setOnboardingCompleted } = useProfileStore.getState();
  const [status, setStatus] = useState<"loading" | "error" | "ready">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * После успешной авторизации проверяет, есть ли ожидающий deep-link токен.
   * Если есть — выполняет auto-scan (вызов AuthService.qrScan) и
   * перенаправляет на страницу подтверждения.
   */
  const handlePostLoginDeepLink = useCallback(async (): Promise<boolean> => {
    if (!hasPendingToken()) return false;

    const pendingToken = consumePendingToken();
    if (!pendingToken) return false;

    console.log(
      "[LoginPage] Deep link token found after login — executing auto-scan:",
      pendingToken,
    );

    try {
      const result = await AuthService.qrScan(pendingToken);
      console.log(
        "[LoginPage] Auto-scan successful, navigating to confirm:",
        result?.qrToken || pendingToken,
      );
      navigate(
        getPath(ConfigRoutes.CONFIRM, {
          qrToken: result?.qrToken || pendingToken,
        }),
        { replace: true },
      );
      return true;
    } catch (err) {
      // Если auto-scan не удался (токен истёк или уже использован),
      // логируем ошибку и продолжаем обычный флоу (навигация на /devices или /onboarding).
      console.warn(
        "[LoginPage] Auto-scan failed, falling back to normal flow:",
        err instanceof Error ? err.message : err,
      );
      return false;
    }
  }, [navigate]);

  useEffect(() => {
    const performLogin = async () => {
      try {
        // initData берётся из VITE_INIT_DATA (.env.dev) в dev-режиме,
        // из window.WebApp (Max Messenger) — во всех остальных.
        // initData is the original URL-encoded string (e.g. "auth_date=...&user=...&hash=..."),
        // user — parsed object for local profile setup.
        const authPayload = getMaxAuthPayload();
        const rawInitData = authPayload?.initData;
        const user = authPayload?.user;

        if (rawInitData && user) {
          // Send the raw initData string to the backend for hash validation
          const result = await AuthService.login(rawInitData);

          // Store tokens
          localStorage.setItem("access_token", result.accessToken);
          localStorage.setItem("refresh_token", result.refreshToken);
          localStorage.setItem("instance_id", String(result.instanceId));

          setTokens(result.accessToken, result.refreshToken, result.instanceId);
          setAuthenticated(true);

          // Set user profile
          setProfile({
            maxId: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            avatar: user.photo_url,
          });

          // ──────────────────────────────────────────────────────
          // Deep Link: проверяем, есть ли ожидающий токен для auto-scan
          // Если да — выполняем сканирование и уходим на confirm page.
          // Если нет — продолжаем обычный флоу (onboarding / devices).
          // ──────────────────────────────────────────────────────
          const handled = await handlePostLoginDeepLink();
          if (handled) return;

          // Check onboarding
          const onboardingDone = localStorage.getItem("onboarding_completed");
          if (onboardingDone) {
            setOnboardingCompleted(true);
            navigate("/devices", { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
        } else {
          // No Max Messenger data — show manual login option
          setStatus("ready");
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(
          error instanceof Error ? error.message : "Authentication failed",
        );
      }
    };

    performLogin();
  }, [
    navigate,
    setTokens,
    setAuthenticated,
    setProfile,
    setOnboardingCompleted,
    handlePostLoginDeepLink,
  ]);

  if (status === "loading") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "2rem",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>🔐</div>
        <h1 style={{ marginBottom: "0.5rem" }}>CryptCloud</h1>
        <p style={{ color: "var(--second-color)" }}>Connecting securely...</p>
        <Loader />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "2rem",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>⚠️</div>
        <h2>Authentication Error</h2>
        <p style={{ color: "var(--accent-color)" }}>{errorMessage}</p>
        <ButtonLarge
          title="Retry"
          onClick={() => {
            setStatus("loading");
            setErrorMessage("");
            window.location.reload();
          }}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Ready state — no Max Messenger data
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      <div style={{ fontSize: "48px", marginBottom: "1rem" }}>🔐</div>
      <h1>CryptCloud</h1>
      <p style={{ color: "var(--second-color)", marginTop: "1rem" }}>
        Please open this MiniApp from Max Messenger to log in automatically.
      </p>
      <ButtonLarge
        title="Try Again"
        onClick={() => {
          setStatus("loading");
          window.location.reload();
        }}
        mode="black"
        Accent={true}
      />
    </div>
  );
};
